import { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Send, Loader2, Sparkles, Minus, Plus, Lock, X, ExternalLink } from 'lucide-react';
import { clsx } from 'clsx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useChat } from '../../hooks/useChat';
import { useAuthStore } from '../../store';
import { getUserLimits, type UserLimits } from '../../utils/api';
import { formatSignal } from '../../utils/format';
import { ToolActivityIndicator } from './ToolActivityIndicator';
import { ChatChart } from './ChatChart';

// Known stock tickers to auto-link (common ones)
const KNOWN_TICKERS = new Set([
  'AAPL', 'MSFT', 'GOOGL', 'GOOG', 'AMZN', 'META', 'NVDA', 'TSLA', 'BRK.A', 'BRK.B',
  'JPM', 'JNJ', 'V', 'PG', 'UNH', 'HD', 'MA', 'DIS', 'PYPL', 'BAC', 'ADBE', 'NFLX',
  'CRM', 'CMCSA', 'XOM', 'VZ', 'INTC', 'T', 'PFE', 'KO', 'PEP', 'MRK', 'WMT', 'ABT',
  'CVX', 'TMO', 'COST', 'NKE', 'MCD', 'ABBV', 'DHR', 'ACN', 'LLY', 'AVGO', 'TXN',
  'NEE', 'UPS', 'PM', 'ORCL', 'AMD', 'LOW', 'HON', 'IBM', 'CAT', 'GE', 'BA', 'GS',
  'MMM', 'SBUX', 'BKNG', 'ISRG', 'MDLZ', 'AXP', 'GILD', 'TGT', 'SPGI', 'BLK', 'SYK',
  'LRCX', 'ZTS', 'CI', 'CB', 'MO', 'SCHW', 'REGN', 'VRTX', 'DE', 'SO', 'DUK', 'CL',
  'CME', 'PLD', 'BDX', 'CSX', 'ICE', 'COP', 'EQIX', 'EOG', 'APD', 'ITW', 'SHW',
  'PINS', 'LULU', 'ABNB', 'PLTR', 'CRWD', 'SNOW', 'DDOG', 'ZS', 'NET', 'COIN', 'RBLX',
  'NVO', 'ASML', 'ARM', 'LMT', 'RTX', 'NOC', 'GD', 'F', 'GM', 'RIVN', 'LCID',
]);

const SUGGESTED_PROMPTS = [
  "What stocks have buy signals?",
  "Explain disconnect scores",
  "Market sentiment overview",
];

// Tier level mapping
const tierLevel: Record<string, number> = {
  'free': 0,
  'starter': 1,
  'early_supporter': 2,
  'pro': 2,
  'enterprise': 3
};

// Free tier daily chat limit
const FREE_TIER_CHAT_LIMIT = 5;

// Check if on app subdomain or localhost (for development)
// In production: true for app.stocksentiment.io
// In development: true for localhost (to test app functionality)
const isAppSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.startsWith('app.') || hostname === 'localhost';
};

// Component to render clickable ticker links
function TickerLink({ ticker, navigate }: { ticker: string; navigate: (path: string) => void }) {
  return (
    <button
      onClick={() => navigate(`/analysis?ticker=${ticker}`)}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--color-accent)]/10 text-[var(--color-accent)] hover:bg-[var(--color-accent)]/20 transition-colors cursor-pointer font-semibold text-sm"
    >
      {ticker}
      <ExternalLink className="h-3 w-3" />
    </button>
  );
}

// Signal types to convert to user-friendly names
const SIGNAL_TYPES = ['STRONG_BUY', 'STRONG_SELL', 'BUY', 'SELL', 'HOLD'];

// Sentiment terms that should be capitalized when appearing as standalone words
const SENTIMENT_TERMS = ['bullish', 'bearish', 'neutral', 'loved', 'hated', 'controversial', 'hyped', 'feared', 'ignored', 'improving', 'declining', 'stable', 'volatile'];

// Process text to linkify stock tickers and format signal types
function processTextWithTickers(text: string, navigate: (path: string) => void): React.ReactNode[] {
  // First, replace signal types with user-friendly names
  let processedText = text;
  for (const signal of SIGNAL_TYPES) {
    // Match the signal as a whole word (not part of another word)
    const signalRegex = new RegExp(`\\b${signal}\\b`, 'g');
    processedText = processedText.replace(signalRegex, formatSignal(signal));
  }

  // Capitalize sentiment terms when they appear at the start of a sentence or standalone
  for (const term of SENTIMENT_TERMS) {
    // Capitalize after period, colon, or at start of text
    const sentenceStartRegex = new RegExp(`(^|[.:]\\s+)(${term})\\b`, 'gi');
    processedText = processedText.replace(sentenceStartRegex, (_, prefix, word) =>
      prefix + word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    );
  }

  // Match standalone tickers (word boundaries, uppercase, 1-5 chars)
  const tickerRegex = /\b([A-Z]{1,5}(?:\.[A-Z])?)\b/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = tickerRegex.exec(processedText)) !== null) {
    const ticker = match[1];
    // Only linkify known tickers
    if (KNOWN_TICKERS.has(ticker)) {
      // Add text before the ticker
      if (match.index > lastIndex) {
        parts.push(processedText.slice(lastIndex, match.index));
      }
      // Add the ticker link
      parts.push(<TickerLink key={`${ticker}-${match.index}`} ticker={ticker} navigate={navigate} />);
      lastIndex = match.index + ticker.length;
    }
  }

  // Add remaining text
  if (lastIndex < processedText.length) {
    parts.push(processedText.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [processedText];
}

export function ChatWidget() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const userTier = user?.subscription_tier || 'free';
  const userTierLevel = tierLevel[userTier] || 0;
  const hasAccess = userTierLevel >= 1; // Starter tier and above have chat access
  const isFreeUser = userTierLevel === 0;

  // Chat usage tracking for free users
  const [userLimits, setUserLimits] = useState<UserLimits | null>(null);
  const chatMessagesToday = userLimits?.usage?.chat_messages_today || 0;
  const chatLimitReached = isFreeUser && chatMessagesToday >= FREE_TIER_CHAT_LIMIT;
  const chatRemaining = isFreeUser ? Math.max(0, FREE_TIER_CHAT_LIMIT - chatMessagesToday) : null;

  const {
    conversations,
    messages,
    isLoading,
    isOpen,
    sendMessage,
    setOpen,
    startNewChat,
    loadConversations,
    loadConversation,
  } = useChat();

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load conversations and user limits on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadConversations();
      // Fetch user limits for free users
      if (isFreeUser) {
        getUserLimits().then(setUserLimits).catch(console.warn);
      }
    }
  }, [isAuthenticated, loadConversations, isFreeUser]);

  // Track whether user is scrolled near the bottom
  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    isNearBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  // Auto-scroll during streaming: instant scrollTop (no smooth animation overlap)
  useEffect(() => {
    if (!isNearBottomRef.current) return;
    const el = messagesContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const input = inputRef.current;
    if (input && input.value.trim()) {
      // Check chat limit for free users
      if (chatLimitReached) {
        return;
      }
      await sendMessage(input.value);
      input.value = '';
      // Refresh limits after sending for free users
      if (isFreeUser) {
        getUserLimits().then(setUserLimits).catch(console.warn);
      }
    }
  };

  // For free users, show button but navigate to chat page (which shows upgrade prompt)
  const handleClick = () => {
    if (!hasAccess) {
      // Navigate to chat page which will show the locked modal
      window.location.href = isAppSubdomain() ? '/chat' : '/app/chat';
    } else {
      setOpen(!isOpen);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={handleClick}
        className={clsx(
          'fixed bottom-6 right-6 z-50 p-4 rounded-full shadow-lg transition-all cursor-pointer',
          'bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)]',
          'text-[var(--bg-primary)]',
          isOpen && hasAccess && 'scale-0 opacity-0'
        )}
        aria-label="Open AI Chat"
      >
        <MessageCircle className="h-6 w-6" />
      </button>

      {/* Chat window - only show for users with access */}
      {hasAccess && (
      <div
        className={clsx(
          'fixed z-50 rounded-xl shadow-2xl transition-all',
          'bg-[var(--bg-primary)] border border-[var(--border-color)]',
          'flex flex-col overflow-hidden',
          // Mobile: full screen with safe area padding
          'inset-3 md:inset-auto',
          // Desktop: fixed size in bottom right
          'md:bottom-6 md:right-6 md:w-[420px]',
          isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
        )}
        style={{
          height: 'auto',
          maxHeight: 'calc(100vh - 24px)',
          minHeight: '400px',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-color)]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[var(--color-accent)]/10 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-[var(--color-accent)]" />
            </div>
            <div>
              <h3 className="font-semibold text-[var(--text-primary)] text-sm">SentimentEdge AI</h3>
              {isFreeUser && chatRemaining !== null && (
                <p className={clsx(
                  'text-xs',
                  chatLimitReached ? 'text-red-400' : 'text-[var(--text-muted)]'
                )}>
                  {chatLimitReached ? 'Daily limit reached' : `${chatRemaining}/${FREE_TIER_CHAT_LIMIT} messages left today`}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={startNewChat}
                className="p-2 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-pointer"
                title="New chat"
              >
                <Plus className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              className="p-2 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-pointer"
              title="Minimize"
            >
              {/* X on mobile, Minus on desktop */}
              <X className="h-5 w-5 md:hidden" />
              <Minus className="h-4 w-4 hidden md:block" />
            </button>
          </div>
        </div>

        {/* Recent conversations (collapsed) */}
        {messages.length === 0 && conversations.length > 0 && (
          <div className="px-4 py-2 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]/50">
            <p className="text-xs text-[var(--text-muted)] mb-2">Recent chats</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {conversations.slice(0, 3).map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => loadConversation(conv.id)}
                  className="flex-shrink-0 px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-card)] hover:bg-[var(--bg-elevated)] border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all truncate max-w-[140px] cursor-pointer"
                >
                  {conv.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <div
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          className="flex-1 overflow-y-auto scrollbar-hide px-4 py-4"
        >
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-12 h-12 rounded-full bg-[var(--color-accent)]/10 flex items-center justify-center mb-4">
                <MessageCircle className="h-6 w-6 text-[var(--color-accent)]" />
              </div>
              <h4 className="font-medium text-[var(--text-primary)] mb-2">
                How can I help?
              </h4>
              <p className="text-sm text-[var(--text-muted)] mb-6">
                Ask about stocks, sentiment, or market analysis.
              </p>
              <div className="space-y-2 w-full">
                {SUGGESTED_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(prompt)}
                    className={clsx(
                      'w-full px-4 py-3 text-sm text-left rounded-lg transition-all cursor-pointer',
                      'bg-[var(--bg-secondary)] hover:bg-[var(--bg-card)] active:scale-[0.98]',
                      'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
                      'border border-[var(--border-color)] hover:border-[var(--color-accent)]/30'
                    )}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {messages.map((message) => (
                <div key={message.id}>
                  {message.role === 'user' ? (
                    <div className="flex justify-end">
                      <div className="max-w-[85%] rounded-2xl rounded-br-md px-4 py-2.5 bg-[var(--color-accent)] text-[var(--bg-primary)]">
                        <p className="text-sm">{message.content}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-[var(--text-primary)] prose-sm">
                      {message.toolActivity && message.toolActivity.length > 0 && (
                        <ToolActivityIndicator activities={message.toolActivity} compact />
                      )}
                      {message.chartData && message.chartData.length > 0 && (
                        message.chartData.map((chart, i) => (
                          <ChatChart key={`chart-${i}`} chart={chart} compact />
                        ))
                      )}
                      {message.content ? (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({ children }) => <h1 className="text-lg font-bold mt-4 mb-2">{children}</h1>,
                            h2: ({ children }) => <h2 className="text-base font-bold mt-4 mb-2">{children}</h2>,
                            h3: ({ children }) => <h3 className="text-sm font-bold mt-3 mb-1.5">{children}</h3>,
                            h4: ({ children }) => <h4 className="text-sm font-semibold mt-3 mb-1">{children}</h4>,
                            p: ({ children }) => {
                              // Process text nodes to linkify tickers
                              const processChildren = (child: React.ReactNode): React.ReactNode => {
                                if (typeof child === 'string') {
                                  return processTextWithTickers(child, navigate);
                                }
                                return child;
                              };
                              const processed = Array.isArray(children)
                                ? children.map((child, i) => <span key={i}>{processChildren(child)}</span>)
                                : processChildren(children);
                              return <p className="text-sm leading-relaxed mb-3">{processed}</p>;
                            },
                            ul: ({ children }) => <ul className="text-sm space-y-1.5 mb-3 ml-4 list-disc">{children}</ul>,
                            ol: ({ children }) => <ol className="text-sm space-y-1.5 mb-3 ml-4 list-decimal">{children}</ol>,
                            li: ({ children }) => {
                              // Process text nodes to linkify tickers in list items
                              const processChildren = (child: React.ReactNode): React.ReactNode => {
                                if (typeof child === 'string') {
                                  return processTextWithTickers(child, navigate);
                                }
                                return child;
                              };
                              const processed = Array.isArray(children)
                                ? children.map((child, i) => <span key={i}>{processChildren(child)}</span>)
                                : processChildren(children);
                              return <li>{processed}</li>;
                            },
                            strong: ({ children }) => {
                              // Also check for tickers in bold text
                              const processChildren = (child: React.ReactNode): React.ReactNode => {
                                if (typeof child === 'string') {
                                  return processTextWithTickers(child, navigate);
                                }
                                return child;
                              };
                              const processed = Array.isArray(children)
                                ? children.map((child, i) => <span key={i}>{processChildren(child)}</span>)
                                : processChildren(children);
                              return <strong className="font-semibold text-[var(--color-accent)]">{processed}</strong>;
                            },
                            a: ({ href, children }) => {
                              // Handle links - check if it's a ticker link
                              const text = typeof children === 'string' ? children : '';
                              if (KNOWN_TICKERS.has(text.toUpperCase())) {
                                return <TickerLink ticker={text.toUpperCase()} navigate={navigate} />;
                              }
                              // Regular external link
                              return (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[var(--color-accent)] hover:underline inline-flex items-center gap-1"
                                >
                                  {children}
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              );
                            },
                            code: ({ children, className }) => {
                              const isInline = !className;
                              return isInline ? (
                                <code className="px-1.5 py-0.5 rounded bg-[var(--bg-secondary)] text-[var(--color-accent)] text-xs font-mono">{children}</code>
                              ) : (
                                <code className={className}>{children}</code>
                              );
                            },
                            pre: ({ children }) => <pre className="p-3 rounded-lg bg-[var(--bg-secondary)] overflow-x-auto mb-3 text-xs">{children}</pre>,
                            table: ({ children }) => (
                              <div className="overflow-x-auto mb-3 rounded-lg border border-[var(--border-color)]">
                                <table className="w-full text-xs">{children}</table>
                              </div>
                            ),
                            thead: ({ children }) => <thead className="bg-[var(--bg-secondary)]">{children}</thead>,
                            tbody: ({ children }) => <tbody className="divide-y divide-[var(--border-color)]">{children}</tbody>,
                            tr: ({ children }) => <tr className="hover:bg-[var(--bg-secondary)]/50">{children}</tr>,
                            th: ({ children }) => <th className="px-3 py-2 text-left font-semibold text-[var(--text-primary)] border-b border-[var(--border-color)]">{children}</th>,
                            td: ({ children }) => <td className="px-3 py-2 text-[var(--text-secondary)]">{children}</td>,
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Thinking...</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

            </div>
          )}
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-[var(--border-color)]">
          {chatLimitReached ? (
            <div className="text-center py-2">
              <div className="flex items-center justify-center gap-2 text-red-400 mb-2">
                <Lock className="h-4 w-4" />
                <span className="text-sm font-medium">Daily limit reached</span>
              </div>
              <p className="text-xs text-[var(--text-muted)]">
                Upgrade to continue chatting or wait until tomorrow.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask about stocks..."
                disabled={isLoading}
                className={clsx(
                  'flex-1 px-4 py-2.5 rounded-lg text-sm',
                  'bg-[var(--bg-secondary)] text-[var(--text-primary)]',
                  'border border-[var(--border-color)]',
                  'placeholder:text-[var(--text-muted)]',
                  'focus:outline-none focus:border-[var(--color-accent)]/50',
                  'disabled:opacity-50 transition-all'
                )}
              />
              <button
                type="submit"
                disabled={isLoading}
                className={clsx(
                  'p-2.5 rounded-lg transition-all',
                  'bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)]',
                  'text-[var(--bg-primary)]',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </button>
            </form>
          )}
        </div>
      </div>
      )}
    </>
  );
}
